import { atom, selector, useRecoilState, useRecoilValue } from 'recoil'

import type { SystemType } from '@/api/@types'
import i18n from '@/lib/i18n'
import type { InputOption } from '@/types'

type TSystemTypeListItem = {
  systemType: SystemType // ex: "rakuten"
  systemTypeName: string // ex: "楽天"
}

const SYSTEM_TYPE_NONE_NAME = i18n.t('purchasedInfo.form.notSpecified')

// state

type TSystemTypeMasterState = {
  // 注文取込向けシステム種別
  forImport: TSystemTypeListItem[]
  // 仕入情報向けシステム種別
  forPurchase: TSystemTypeListItem[]
}

export const systemTypesMasterState = atom<TSystemTypeMasterState>({
  key: 'systemTypesMasterState',
  default: {
    forImport: [],
    forPurchase: [],
  },
})

// selector

const systemTypesDictionaries = selector({
  key: 'systemTypesDictionary',
  get: ({ get }) => {
    const master = get(systemTypesMasterState)

    function createDict(items: TSystemTypeListItem[]) {
      const dict = {} as {
        [key in TSystemTypeListItem['systemType']]: TSystemTypeListItem['systemTypeName']
      }
      items.forEach((item) => {
        dict[item.systemType] = item.systemTypeName
      })
      return dict
    }

    return {
      forImport: createDict(master.forImport),
      forPurchase: createDict(master.forPurchase),
    }
  },
})

const systemTypesInputOptions = selector({
  key: 'systemTypesOptions',
  get: ({ get }) => {
    const master = get(systemTypesMasterState)

    function createOptions(
      items: TSystemTypeListItem[],
      config: {
        excludeTypes: SystemType[]
      } = {
        excludeTypes: [],
      }
    ) {
      const options: InputOption<SystemType | ''>[] = [
        {
          label: SYSTEM_TYPE_NONE_NAME,
          value: '',
        },
        ...items
          .map((item) => ({
            label: item.systemTypeName,
            value: item.systemType,
          }))
          .filter((i) => !config.excludeTypes.includes(i.value)),
      ]
      return options
    }

    return {
      forImport: createOptions(master.forImport, {
        excludeTypes: ['tamago'], // FIXME: たまごリピートを選択肢から除外する。バックエンドのレスポンスから消えた場合、この処理を削除する
      }),
      forPurchase: createOptions(master.forPurchase, {
        excludeTypes: ['tamago'], // FIXME: たまごリピートを選択肢から除外する。バックエンドのレスポンスから消えた場合、この処理を削除する
      }),
    }
  },
})

// hooks - state

export const useSystemTypesMaster = () => {
  const [systemTypesMaster, setSystemTypesMaster] = useRecoilState(
    systemTypesMasterState
  )
  return { systemTypesMaster, setSystemTypesMaster }
}

// hooks - selector

export const useSystemTypesDictionaries = () => {
  return useRecoilValue(systemTypesDictionaries)
}

export const useSystemTypesInputOptions = () => {
  return useRecoilValue(systemTypesInputOptions)
}
